import { DiscoverySource } from 'api/clients/UserClient'
import { Brand } from 'hooks/useBrand'
import { HeapEventType, trackEventInHeap } from 'utils/heapTracking'

export const loginAnalyticsParams = [
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_source',
  'utm_term',
  'referral_page',
  'referrer',
]

export const totalCompletableSteps = 4

export enum RegistrationType {
  Google = 'google',
  Facebook = 'facebook',
  Apple = 'apple',
  EmailAndPassword = 'emailAndPassword',
  Invited = 'invited',
  Unknown = '',
}

export const hideEmail = (email: string) => {
  return email.replace(/(\w{3}).+@([\w.]+\w)/, '$1***@$2')
}

export const companyInitials = (name: string) => {
  if (!name) {
    return ''
  }

  return name.split(' ').reduce((acc, elem) => {
    if (acc.length === 2) {
      return acc
    }

    if (elem[0]) {
      return acc + elem[0].toUpperCase()
    }

    return acc
  }, '')
}
export const trackRegistrationStart = (type: RegistrationType) => {
  trackEventInHeap(HeapEventType.Registration, { action: 'start', type })
}
export const trackRegistrationEnd = (type: RegistrationType) => {
  trackEventInHeap(HeapEventType.Registration, { action: 'end', type })
}
export const trackRegistrationSkipInvites = (type: RegistrationType) => {
  trackEventInHeap(HeapEventType.Registration, { action: 'skip_invites', type })
}
export const trackCancelWorkspaceCreation = (type: RegistrationType) => {
  trackEventInHeap(HeapEventType.Registration, { action: 'cancel_workspace_creation', type })
}
export const trackContinueWithLogin = () => {
  trackEventInHeap(HeapEventType.Registration, { action: 'enter-email-google-complete', type: 'login' })
}
export const trackContinueWithSignUp = () => {
  trackEventInHeap(HeapEventType.Registration, { action: 'enter-email-google-complete', type: 'signup' })
}
export const trackVerifyWithCodeComplete = () => {
  trackEventInHeap(HeapEventType.Registration, { action: 'verify-email-with-code-complete' })
}
export const trackVerifyWithCodeFailed = (error: string) => {
  trackEventInHeap(HeapEventType.Registration, { action: 'verify-email-with-code-failed', type: error })
}
export const trackCreateAccountComplete = () => {
  trackEventInHeap(HeapEventType.Registration, { action: 'create-account-complete' })
}
export const trackCreateAccountFailed = (error: string) => {
  trackEventInHeap(HeapEventType.Registration, { action: 'create-account-failed', type: error })
}
export const trackSelectRoleComplete = (role: string) => {
  trackEventInHeap(HeapEventType.Registration, { action: 'select-role-complete', type: role })
}
export const trackSelectBusinessTypeComplete = (businessType: string) => {
  trackEventInHeap(HeapEventType.Registration, { action: 'select-business-type-complete', type: businessType })
}
export const trackSelectDiscoverySourceComplete = (discoverySource: DiscoverySource) => {
  trackEventInHeap(HeapEventType.Registration, { action: 'select-discovery-source-complete', type: discoverySource })
}
export const trackSelectUseCaseComplete = (useCase: string) => {
  trackEventInHeap(HeapEventType.Registration, { action: 'select-use-case-complete', type: useCase })
}
export const trackSelectUseCaseFailed = (error: string) => {
  trackEventInHeap(HeapEventType.Registration, { action: 'select-use-case-failed', type: error })
}
export const titleTag = (isDoc: boolean, brand: Brand) => {
  return isDoc ? `Create and collaborate in ${brand.name}` : `${brand.name} | ${brand.shortDescription}`
}
export const descriptionTag = (isDoc: boolean, brand: Brand) => {
  return isDoc
    ? 'Let your work shine. Write, evolve, and share your ideas in beautiful, powerful documents.'
    : brand.longDescription
}

export const splitUserName = (fullName: string) => {
  const nameArr = fullName.split(' ')
  const firstName = nameArr.shift()
  const lastName = nameArr.join(' ')
  return { firstName, lastName }
}

export const hashEmailToPercentage = (email: string) => {
  if (!email) return 0

  const normalizedEmail = email.trim().toLowerCase()

  let hash = 0
  for (let i = 0; i < normalizedEmail.length; i++) {
    hash = (hash << 5) - hash + normalizedEmail.charCodeAt(i)
    hash |= 0 // Convert to 32bit integer
  }

  // Normalize to a value between 0 and 1
  return Math.abs(hash) / 2147483647 // Max 32bit signed int
}
